import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

export default function Forum() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "General", title: "", body: "" });

  useEffect(() => {
    api.get("/forum/categories").then((r) => setCats(["All", ...r.data])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const q = active !== "All" ? `?category=${encodeURIComponent(active)}` : "";
    const { data } = await api.get(`/forum/threads${q}`);
    setThreads(data);
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/forum/threads", form);
      toast.success("Thread posted");
      setOpen(false);
      setForm({ category: "General", title: "", body: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="pt-28 md:pt-36" data-testid="forum-page">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Community</p>
            <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-6xl">
              The <span className="italic text-velvet-rose">Lounge</span>
            </h1>
          </Reveal>
          <button
            onClick={() => (user ? setOpen(true) : toast.error("Please sign in to post."))}
            data-testid="forum-new-thread"
            className="flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white"
          >
            <Plus size={15} /> New thread
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`forum-cat-${c.toLowerCase()}`}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-luxe transition-colors duration-300 ${
                active === c ? "border-velvet-rose bg-velvet-rose text-black" : "border-white/15 text-white/60 hover:border-velvet-rose"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
          {threads.length === 0 && <p className="py-12 text-center text-sm font-light text-white/40">No threads yet — start the conversation.</p>}
          {threads.map((t) => (
            <Link key={t.id} to={`/forum/${t.id}`} data-testid={`thread-${t.id}`} className="group flex items-center justify-between gap-6 py-6">
              <div>
                <span className="text-[10px] uppercase tracking-luxe text-velvet-gold">{t.category}</span>
                <h3 className="mt-1 font-heading text-2xl font-normal text-white transition-colors group-hover:text-velvet-rose">{t.title}</h3>
                <p className="mt-1 text-xs text-white/40">by {t.author_name}</p>
              </div>
              <span className="flex items-center gap-2 text-sm text-white/50"><MessageSquare size={15} /> {t.reply_count}</span>
            </Link>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-6 backdrop-blur-xl" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-lg border border-white/10 bg-velvet-coal p-8" data-testid="forum-thread-form">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-heading text-2xl text-white">New thread</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="text-white/60" /></button>
            </div>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} mb-4`}>
              {cats.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-velvet-coal">{c}</option>)}
            </select>
            <input required placeholder="Title" value={form.title} data-testid="forum-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} mb-4`} />
            <textarea required rows={4} placeholder="Say something…" value={form.body} data-testid="forum-body" onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} mb-6`} />
            <button type="submit" data-testid="forum-submit" className="w-full rounded-full bg-white py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white">Post thread</button>
          </form>
        </div>
      )}
    </div>
  );
}
